import React, { useState, useCallback } from 'react';
import { haversineKm, fmtKm } from '../utils/distance';
import TempleCard from './TempleCard';
import './NearbyView.css';

const RADIUS_KM = 50;

export default function NearbyView({ lang }) {
  const L = (en, hi) => lang === 'en' ? en : hi;

  const [phase, setPhase]       = useState('idle');
  const [userLoc, setUserLoc]   = useState(null);
  const [nearby, setNearby]     = useState([]);
  const [geoError, setGeoError] = useState('');

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError(L('Geolocation is not supported by your browser.', 'आपका ब्राउज़र जियोलोकेशन का समर्थन नहीं करता।'));
      setPhase('error');
      return;
    }

    setPhase('locating');

    navigator.geolocation.getCurrentPosition(
      async pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setUserLoc({ lat, lng });
        setPhase('loading');

        try {
          const idx = await fetch(`${process.env.PUBLIC_URL}/data/states_index.json`).then(r => r.json());
          const stateArrays = await Promise.all(
            idx.map(async s => {
              const raw = await fetch(`${process.env.PUBLIC_URL}/data/${s.file}`).then(r => r.json());
              const list = Array.isArray(raw) ? raw : raw.temples;
              return list.map(t => ({ ...t, state_en: s.name_en, state_hi: s.name_hi }));
            })
          );
          const all = stateArrays.flat();

          const results = all
            .filter(t => t.lat && t.lng)
            .map(t => ({ ...t, _distance: haversineKm(lat, lng, t.lat, t.lng) }))
            .filter(t => t._distance <= RADIUS_KM)
            .sort((a, b) => a._distance - b._distance);

          setNearby(results);
          setPhase('done');
        } catch {
          setGeoError(L('Failed to load temple data. Please try again.', 'मंदिर डेटा लोड करने में विफल। कृपया पुनः प्रयास करें।'));
          setPhase('error');
        }
      },
      err => {
        const msgs = {
          1: L('Location access was denied. Please allow location access in your browser settings.', 'स्थान पहुंच अस्वीकृत। ब्राउज़र सेटिंग में अनुमति दें।'),
          2: L('Your location could not be determined. Please try again.', 'आपका स्थान निर्धारित नहीं हो सका।'),
          3: L('Location request timed out. Please try again.', 'स्थान अनुरोध समय समाप्त हो गया।'),
        };
        setGeoError(msgs[err.code] || L('An unknown error occurred.', 'एक अज्ञात त्रुटि हुई।'));
        setPhase('error');
      },
      { timeout: 12000, enableHighAccuracy: false }
    );
  }, [lang]); // eslint-disable-line

  if (phase === 'idle') {
    return (
      <div className="nearby-idle fade-in">
        <div className="nearby-idle-icon">📍</div>
        <h2 className="nearby-idle-title">{L('Temples Near You', 'आपके पास मंदिर')}</h2>
        <p className="nearby-idle-desc">
          {L(
            `Find sacred sites within ${RADIUS_KM} km of your current location, sorted by distance. Covers all 5 South Indian states.`,
            `आपके वर्तमान स्थान से ${RADIUS_KM} km के भीतर पवित्र स्थल खोजें। पाँचों दक्षिण भारतीय राज्यों को कवर करता है।`
          )}
        </p>
        <button className="nearby-locate-btn" onClick={handleLocate}>
          {L('Find Temples Near Me', 'मेरे पास मंदिर खोजें')}
        </button>
        <p className="nearby-idle-note">
          {L('Your location is used only in this browser and is never stored or sent anywhere.', 'आपका स्थान केवल इस ब्राउज़र में उपयोग किया जाता है, कहीं संग्रहीत या भेजा नहीं जाता।')}
        </p>
      </div>
    );
  }

  if (phase === 'locating') {
    return (
      <div className="nearby-loading fade-in">
        <div className="nearby-spinner" />
        <p className="nearby-loading-text">{L('Getting your location…', 'आपका स्थान प्राप्त हो रहा है…')}</p>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="nearby-loading fade-in">
        <div className="nearby-spinner" />
        <p className="nearby-loading-text">{L('Loading all temples…', 'सभी मंदिर लोड हो रहे हैं…')}</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="nearby-error fade-in">
        <span className="nearby-error-icon">⚠️</span>
        <p className="nearby-error-msg">{geoError}</p>
        <button className="nearby-locate-btn" onClick={handleLocate}>
          {L('Try Again', 'पुनः प्रयास करें')}
        </button>
      </div>
    );
  }

  return (
    <div className="nearby-results fade-in">
      <div className="nearby-results-header">
        <div className="nearby-results-title-row">
          <h2 className="nearby-results-title">
            {nearby.length > 0
              ? L(`${nearby.length} temple${nearby.length === 1 ? '' : 's'} within ${RADIUS_KM} km`, `${RADIUS_KM} km के भीतर ${nearby.length} मंदिर`)
              : L(`No temples found within ${RADIUS_KM} km`, `${RADIUS_KM} km के भीतर कोई मंदिर नहीं`)}
          </h2>
          <button className="nearby-retry-btn" onClick={() => setPhase('idle')}>
            {L('← Change location', '← स्थान बदलें')}
          </button>
        </div>
        {userLoc && (
          <p className="nearby-results-loc">
            📍 {userLoc.lat.toFixed(4)}°N, {userLoc.lng.toFixed(4)}°E
            {nearby.length > 0 && ` · ${L('closest', 'सबसे पास')}: ${fmtKm(nearby[0]._distance)}`}
          </p>
        )}
      </div>

      {nearby.length === 0 ? (
        <div className="nearby-empty">
          <span className="nearby-empty-icon">🗺️</span>
          <p className="nearby-empty-msg">
            {L(
              'No sacred sites found within 50 km. Sacred South India covers Tamil Nadu, Karnataka, Kerala, Andhra Pradesh, and Telangana.',
              '50 km के भीतर कोई पवित्र स्थल नहीं मिला। Sacred South India में तमिलनाडु, कर्नाटक, केरल, आंध्र प्रदेश और तेलंगाना शामिल हैं।'
            )}
          </p>
        </div>
      ) : (
        <div className="temple-grid">
          {nearby.map(t => (
            <TempleCard key={t.id} temple={t} lang={lang} distance={t._distance} />
          ))}
        </div>
      )}
    </div>
  );
}
